import { useCallback, useEffect, useRef, useState } from "react";

// Generic loader for the admin/saved-tests pages.
// - calls `fn` on mount (and via reload())
// - on network-down (ApiError status 0) → uses `fallback` + sets demo=true
// - on other errors → sets error message
// `fn` and `fallback` are captured in refs so callers can pass inline values
// without re-running the effect.
export function useApiData(fn, fallback = null) {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const fallbackRef = useRef(fallback);

  // Start EMPTY, not with the fallback.
  //
  // Seeding state with `fallback` meant every page rendered its demo fixture for
  // the duration of the first request — and because `demo` was still false, the
  // "showing demo data" banner was hidden, so the placeholders looked like real
  // records. On the Project Managers tab that read as "1 invitation pending —
  // tom@acme.com", a person who doesn't exist. The fallback is for one specific
  // case (the backend is unreachable) and is applied there, in the catch.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demo, setDemo] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await fnRef.current();
      setData(d);
      setDemo(false);
      return d;
    } catch (e) {
      if (e && e.status === 0) {
        setDemo(true);
        setData(fallbackRef.current);
      } else {
        setError((e && e.message) || "Failed to load.");
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, demo, error, reload: load, setData };
}
